import React, { useState, useEffect } from "react";
import Modal from "react-modal";
import Swal from "sweetalert2";
import axiosInstance from "../../config/axiosConfig";
import { CircularProgress } from "@mui/material";
import { Link } from "react-router-dom";

const customModalStyles = {
  content: {
    padding: "24px",
    width: "90%",
    maxWidth: "600px",
    margin: "auto",
    borderRadius: "12px",
    boxShadow: "0 20px 45px rgba(0, 0, 0, 0.18)",
    border: "1px solid #e2e8f0",
    maxHeight: "90vh",
    overflowY: "auto",
  },
  overlay: {
    backgroundColor: "rgba(15, 23, 42, 0.6)",
    zIndex: 10000,
  },
};

const CATEGORY_OPTIONS = [
  "Eligible Candidates",
  "Ineligible Candidates",
  "Shortlisted for Interview",
  "Interview Schedule",
  "Scrutiny / General List",
];

const getCategoryColor = (cat = "") => {
  const c = cat.toLowerCase();
  if (c.includes("ineligible")) return { bg: "#fee2e2", text: "#991b1b", border: "#fca5a5" };
  if (c.includes("eligible")) return { bg: "#dcfce7", text: "#166534", border: "#86efac" };
  if (c.includes("interview") || c.includes("shortlist")) return { bg: "#e0e7ff", text: "#3730a3", border: "#a5b4fc" };
  return { bg: "#f1f5f9", text: "#334155", border: "#cbd5e1" };
};

const parseBooleanDownloadable = (val) => {
  if (val === false || val === 0 || val === "0" || val === "false") {
    return false;
  }
  return true;
};

const CareerApplicants = () => {
  const [applicantLists, setApplicantLists] = useState([]);
  const [careers, setCareers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [buttonLoading, setButtonLoading] = useState(false);
  const [selectedJobFilter, setSelectedJobFilter] = useState("ALL");
  const [searchQuery, setSearchQuery] = useState("");

  const [showModal, setShowModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editId, setEditId] = useState(null);

  const [formData, setFormData] = useState({
    careerId: "",
    customJobTitle: "",
    title: "",
    category: "Eligible Candidates",
    sortOrder: 0,
    isActive: true,
    isDownloadable: true,
  });
  const [selectedFile, setSelectedFile] = useState(null);
  const [existingPdfUrl, setExistingPdfUrl] = useState(null);

  useEffect(() => {
    fetchApplicantLists();
    fetchCareers();
  }, []);

  const fetchApplicantLists = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get("career-applicants/all?all=true");
      setApplicantLists(response?.data?.data || []);
    } catch (error) {
      console.error("Error fetching applicant lists:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCareers = async () => {
    try {
      const response = await axiosInstance.get("career/all");
      setCareers(response?.data?.data || []);
    } catch (error) {
      console.error("Error fetching careers:", error);
    }
  };

  const handleOpenCreate = () => {
    setIsEditing(false);
    setEditId(null);
    setFormData({
      careerId: careers[0]?._id || "",
      customJobTitle: "",
      title: "",
      category: "Eligible Candidates",
      sortOrder: 0,
      isActive: true,
      isDownloadable: true,
    });
    setSelectedFile(null);
    setExistingPdfUrl(null);
    setShowModal(true);
  };

  const handleOpenEdit = (item) => {
    setIsEditing(true);
    setEditId(item._id);
    setFormData({
      careerId: item.careerId || (item.career?._id || "OTHER"),
      customJobTitle: item.careerId ? "" : item.jobTitle || "",
      title: item.title || "",
      category: item.category || "Eligible Candidates",
      sortOrder: item.sortOrder ?? 0,
      isActive: item.isActive !== false,
      isDownloadable: parseBooleanDownloadable(item.isDownloadable),
    });
    setSelectedFile(null);
    setExistingPdfUrl(item.filePath || item.downloadUrl || null);
    setShowModal(true);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.type !== "application/pdf" && !file.name.toLowerCase().endsWith(".pdf")) {
        Swal.fire("Invalid File", "Please upload a valid PDF document.", "warning");
        e.target.value = "";
        return;
      }
      const sizeMB = file.size / (1024 * 1024);
      if (sizeMB > 50) {
        Swal.fire("File Too Large", "Maximum allowed PDF file size is 50MB.", "warning");
        e.target.value = "";
        return;
      }
      setSelectedFile(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.title.trim()) {
      Swal.fire("Validation Error", "Please provide a list title.", "warning");
      return;
    }

    if (!isEditing && !selectedFile) {
      Swal.fire("Validation Error", "Please select a PDF document to upload.", "warning");
      return;
    }

    setButtonLoading(true);

    try {
      const dataToSend = new FormData();
      dataToSend.append("title", formData.title.trim());
      dataToSend.append("category", formData.category);
      dataToSend.append("sortOrder", formData.sortOrder);
      dataToSend.append("isActive", formData.isActive ? "1" : "0");
      dataToSend.append("isDownloadable", formData.isDownloadable ? "1" : "0");

      if (formData.careerId === "OTHER" || !formData.careerId) {
        dataToSend.append("careerId", "");
        dataToSend.append("jobTitle", formData.customJobTitle || "General / Statutory Positions");
      } else {
        dataToSend.append("careerId", formData.careerId);
        const matchedCareer = careers.find((c) => String(c._id) === String(formData.careerId));
        if (matchedCareer) {
          dataToSend.append("jobTitle", matchedCareer.title);
        }
      }

      if (selectedFile) {
        dataToSend.append("file", selectedFile);
      }

      if (isEditing) {
        dataToSend.append("_method", "PUT");
        await axiosInstance.post(`career-applicants/change/${editId}`, dataToSend);
        Swal.fire("Success!", "Applicant list updated successfully!", "success");
      } else {
        await axiosInstance.post("career-applicants/create", dataToSend);
        Swal.fire("Success!", "Applicant list uploaded successfully!", "success");
      }

      setShowModal(false);
      fetchApplicantLists();
    } catch (error) {
      console.error("Error saving applicant list:", error);
      const msg = error.response?.data?.message || "Failed to save applicant list.";
      Swal.fire("Error", msg, "error");
    } finally {
      setButtonLoading(false);
    }
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "This applicant list PDF will be permanently removed.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#ef4444",
      cancelButtonColor: "#64748b",
      confirmButtonText: "Yes, delete it!",
    });

    if (result.isConfirmed) {
      try {
        await axiosInstance.delete(`career-applicants/delete/${id}`);
        Swal.fire("Deleted!", "Applicant list has been deleted.", "success");
        fetchApplicantLists();
      } catch (error) {
        console.error("Error deleting applicant list:", error);
        Swal.fire("Error", "Failed to delete applicant list.", "error");
      }
    }
  };

  // Filter & Search
  const filteredLists = applicantLists.filter((item) => {
    if (selectedJobFilter !== "ALL") {
      const matchId = String(item.careerId || item.career?._id || "");
      if (matchId !== selectedJobFilter) return false;
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      const matchTitle = (item.title || "").toLowerCase().includes(q);
      const matchJob = (item.jobTitle || "").toLowerCase().includes(q);
      const matchCat = (item.category || "").toLowerCase().includes(q);
      if (!matchTitle && !matchJob && !matchCat) return false;
    }
    return true;
  });

  return (
    <div style={{ padding: "10px 0", maxWidth: "1280px", margin: "0 auto" }}>
      {/* Top Navigation Tabs */}
      <div style={{ display: "flex", gap: "12px", borderBottom: "2px solid #e2e8f0", marginBottom: "24px" }}>
        <Link
          to="/career"
          style={{
            padding: "10px 20px",
            textDecoration: "none",
            color: "#64748b",
            fontWeight: 600,
            fontSize: "15px",
            borderBottom: "3px solid transparent",
            transition: "all 0.2s",
          }}
        >
          💼 Job Openings
        </Link>
        <Link
          to="/career/applicants"
          style={{
            padding: "10px 20px",
            textDecoration: "none",
            color: "#2563eb",
            fontWeight: 700,
            fontSize: "15px",
            borderBottom: "3px solid #2563eb",
            backgroundColor: "#eff6ff",
            borderRadius: "6px 6px 0 0",
          }}
        >
          📋 List of Applicants
        </Link>
      </div>

      {/* Header & Actions */}
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "space-between",
          alignItems: "center",
          gap: "16px",
          marginBottom: "20px",
        }}
      >
        <div>
          <h2 style={{ margin: "0 0 4px", fontSize: "22px", color: "#0f172a", fontWeight: 700 }}>
            Job Applicant Lists
          </h2>
          <p style={{ margin: 0, fontSize: "13px", color: "#64748b" }}>
            Upload and manage official candidate lists, scrutiny results, and interview schedules.
          </p>
        </div>

        <button
          onClick={handleOpenCreate}
          style={{
            padding: "11px 22px",
            backgroundColor: "#2563eb",
            color: "#fff",
            border: "none",
            borderRadius: "8px",
            fontWeight: 600,
            fontSize: "14px",
            cursor: "pointer",
            boxShadow: "0 4px 12px rgba(37, 99, 235, 0.25)",
            display: "flex",
            alignItems: "center",
            gap: "8px",
          }}
        >
          <span>+ Upload Applicant List</span>
        </button>
      </div>

      {/* Filter Bar */}
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: "12px",
          alignItems: "center",
          background: "#f8fafc",
          padding: "14px 18px",
          borderRadius: "10px",
          border: "1px solid #e2e8f0",
          marginBottom: "22px",
        }}
      >
        <div style={{ flex: 1, minWidth: "220px" }}>
          <input
            type="text"
            placeholder="🔍 Search lists by title or job name..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{
              width: "100%",
              padding: "9px 14px",
              borderRadius: "6px",
              border: "1px solid #cbd5e1",
              fontSize: "13.5px",
              boxSizing: "border-box",
            }}
          />
        </div>

        <div style={{ minWidth: "220px" }}>
          <select
            value={selectedJobFilter}
            onChange={(e) => setSelectedJobFilter(e.target.value)}
            style={{
              width: "100%",
              padding: "9px 12px",
              borderRadius: "6px",
              border: "1px solid #cbd5e1",
              fontSize: "13.5px",
              backgroundColor: "#ffffff",
              boxSizing: "border-box",
            }}
          >
            <option value="ALL">All Jobs ({applicantLists.length})</option>
            {careers.map((c) => (
              <option key={c._id} value={c._id}>
                {c.title}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Table / List Container */}
      {loading ? (
        <div style={{ textAlign: "center", padding: "60px 0" }}>
          <CircularProgress />
          <p style={{ marginTop: "12px", color: "#64748b", fontSize: "14px" }}>Loading applicant lists...</p>
        </div>
      ) : filteredLists.length === 0 ? (
        <div
          style={{
            textAlign: "center",
            padding: "50px 20px",
            background: "#ffffff",
            borderRadius: "12px",
            border: "1.5px dashed #cbd5e1",
          }}
        >
          <div style={{ fontSize: "36px", marginBottom: "8px" }}>📄</div>
          <h3 style={{ margin: "0 0 6px", color: "#1e293b" }}>No Applicant Lists Found</h3>
          <p style={{ margin: "0 0 16px", color: "#64748b", fontSize: "13.5px" }}>
            {searchQuery || selectedJobFilter !== "ALL"
              ? "No lists match your search or filter."
              : "Upload your first applicant list PDF using the button above."}
          </p>
          <button
            onClick={handleOpenCreate}
            style={{
              padding: "9px 18px",
              background: "#2563eb",
              color: "#fff",
              border: "none",
              borderRadius: "6px",
              cursor: "pointer",
              fontWeight: 600,
            }}
          >
            Upload Applicant List
          </button>
        </div>
      ) : (
        <div
          style={{
            background: "#ffffff",
            borderRadius: "12px",
            boxShadow: "0 2px 10px rgba(0,0,0,0.06)",
            border: "1px solid #e2e8f0",
            overflow: "hidden",
          }}
        >
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", textAlign: "left", fontSize: "13.5px" }}>
              <thead>
                <tr style={{ backgroundColor: "#f8fafc", borderBottom: "1px solid #e2e8f0", color: "#475569" }}>
                  <th style={{ padding: "14px 16px", fontWeight: 700 }}>Job Position</th>
                  <th style={{ padding: "14px 16px", fontWeight: 700 }}>Applicant List Title</th>
                  <th style={{ padding: "14px 16px", fontWeight: 700 }}>Category</th>
                  <th style={{ padding: "14px 16px", fontWeight: 700 }}>PDF Document</th>
                  <th style={{ padding: "14px 16px", fontWeight: 700 }}>Downloadable</th>
                  <th style={{ padding: "14px 16px", fontWeight: 700 }}>Date</th>
                  <th style={{ padding: "14px 16px", fontWeight: 700 }}>Status</th>
                  <th style={{ padding: "14px 16px", fontWeight: 700, textAlign: "right" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredLists.map((item, idx) => {
                  const catStyle = getCategoryColor(item.category);
                  const pdfUrl = item.filePath || item.downloadUrl;
                  const dateStr = item.createdAt ? new Date(item.createdAt).toLocaleDateString() : "-";

                  return (
                    <tr
                      key={item._id || idx}
                      style={{
                        borderBottom: "1px solid #f1f5f9",
                        backgroundColor: idx % 2 === 0 ? "#ffffff" : "#fcfdfe",
                        transition: "background-color 0.15s",
                      }}
                    >
                      {/* Job Title */}
                      <td style={{ padding: "14px 16px", fontWeight: 600, color: "#1e293b", maxWidth: "200px" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                          <span style={{ fontSize: "15px" }}>💼</span>
                          <span style={{ wordBreak: "break-word" }}>
                            {item.jobTitle || item.career?.title || "General / Statutory"}
                          </span>
                        </div>
                      </td>

                      {/* List Title */}
                      <td style={{ padding: "14px 16px", color: "#0f172a", fontWeight: 600, maxWidth: "260px" }}>
                        <div style={{ wordBreak: "break-word" }}>{item.title}</div>
                      </td>

                      {/* Category Badge */}
                      <td style={{ padding: "14px 16px" }}>
                        <span
                          style={{
                            display: "inline-block",
                            padding: "3px 10px",
                            borderRadius: "14px",
                            fontSize: "12px",
                            fontWeight: 600,
                            backgroundColor: catStyle.bg,
                            color: catStyle.text,
                            border: `1px solid ${catStyle.border}`,
                            whiteSpace: "nowrap",
                          }}
                        >
                          {item.category || "General"}
                        </span>
                      </td>

                      {/* PDF Document */}
                      <td style={{ padding: "14px 16px" }}>
                        {pdfUrl ? (
                          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                            <a
                              href={pdfUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "4px",
                                padding: "4px 10px",
                                backgroundColor: "#eff6ff",
                                color: "#1d4ed8",
                                borderRadius: "6px",
                                textDecoration: "none",
                                fontWeight: 600,
                                fontSize: "12px",
                                border: "1px solid #bfdbfe",
                              }}
                            >
                              <span>📥 View PDF</span>
                            </a>
                            {item.fileSize && (
                              <span style={{ fontSize: "11px", color: "#64748b", fontWeight: 500 }}>
                                ({item.fileSize})
                              </span>
                            )}
                          </div>
                        ) : (
                          <span style={{ color: "#94a3b8", fontSize: "12px" }}>No File</span>
                        )}
                      </td>

                      {/* Downloadable */}
                      <td style={{ padding: "14px 16px" }}>
                        <span
                          style={{
                            display: "inline-block",
                            padding: "2px 8px",
                            borderRadius: "12px",
                            fontSize: "11px",
                            fontWeight: 700,
                            backgroundColor: parseBooleanDownloadable(item.isDownloadable) ? "#e0f2fe" : "#fef3c7",
                            color: parseBooleanDownloadable(item.isDownloadable) ? "#0369a1" : "#92400e",
                          }}
                        >
                          {parseBooleanDownloadable(item.isDownloadable) ? "✓ Downloadable" : "🔒 View Only"}
                        </span>
                      </td>

                      {/* Date */}
                      <td style={{ padding: "14px 16px", color: "#64748b", fontSize: "12.5px" }}>
                        {dateStr}
                      </td>

                      {/* Status */}
                      <td style={{ padding: "14px 16px" }}>
                        <span
                          style={{
                            display: "inline-block",
                            padding: "2px 8px",
                            borderRadius: "12px",
                            fontSize: "11px",
                            fontWeight: 700,
                            backgroundColor: item.isActive !== false ? "#dcfce7" : "#f1f5f9",
                            color: item.isActive !== false ? "#15803d" : "#64748b",
                          }}
                        >
                          {item.isActive !== false ? "Active" : "Hidden"}
                        </span>
                      </td>

                      {/* Actions */}
                      <td style={{ padding: "14px 16px", textAlign: "right", whiteSpace: "nowrap" }}>
                        <button
                          onClick={() => handleOpenEdit(item)}
                          style={{
                            padding: "5px 12px",
                            backgroundColor: "#f8fafc",
                            border: "1px solid #cbd5e1",
                            borderRadius: "5px",
                            color: "#334155",
                            fontWeight: 600,
                            fontSize: "12px",
                            cursor: "pointer",
                            marginRight: "6px",
                          }}
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDelete(item._id)}
                          style={{
                            padding: "5px 12px",
                            backgroundColor: "#fee2e2",
                            border: "1px solid #fca5a5",
                            borderRadius: "5px",
                            color: "#b91c1c",
                            fontWeight: 600,
                            fontSize: "12px",
                            cursor: "pointer",
                          }}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Upload / Edit Modal */}
      <Modal
        isOpen={showModal}
        onRequestClose={() => !buttonLoading && setShowModal(false)}
        style={customModalStyles}
        contentLabel={isEditing ? "Edit Applicant List" : "Upload Applicant List"}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
          <h3 style={{ margin: 0, fontSize: "18px", color: "#0f172a", fontWeight: 700 }}>
            {isEditing ? "✏️ Edit Applicant List" : "📤 Upload List of Applicants"}
          </h3>
          <button
            type="button"
            onClick={() => setShowModal(false)}
            style={{
              background: "none",
              border: "none",
              fontSize: "20px",
              cursor: "pointer",
              color: "#94a3b8",
            }}
          >
            ✕
          </button>
        </div>

        <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
          {/* Select Job */}
          <div>
            <label style={{ display: "block", fontSize: "13px", fontWeight: 700, color: "#334155", marginBottom: "6px" }}>
              Select Job Position *
            </label>
            <select
              value={formData.careerId}
              onChange={(e) => setFormData({ ...formData, careerId: e.target.value })}
              style={{
                width: "100%",
                padding: "9px 12px",
                borderRadius: "6px",
                border: "1px solid #cbd5e1",
                fontSize: "13.5px",
                boxSizing: "border-box",
                backgroundColor: "#fff",
              }}
            >
              {careers.map((c) => (
                <option key={c._id} value={c._id}>
                  {c.title} {c.scaleRange ? `(${c.scaleRange})` : ""}
                </option>
              ))}
              <option value="OTHER">Other / General / Statutory Positions</option>
            </select>
          </div>

          {/* Custom Job Title if OTHER selected */}
          {(formData.careerId === "OTHER" || !formData.careerId) && (
            <div>
              <label style={{ display: "block", fontSize: "13px", fontWeight: 700, color: "#334155", marginBottom: "6px" }}>
                Custom Job / Position Title *
              </label>
              <input
                type="text"
                placeholder="e.g. Statutory Positions / Administrative Staff"
                value={formData.customJobTitle}
                onChange={(e) => setFormData({ ...formData, customJobTitle: e.target.value })}
                style={{
                  width: "100%",
                  padding: "9px 12px",
                  borderRadius: "6px",
                  border: "1px solid #cbd5e1",
                  fontSize: "13.5px",
                  boxSizing: "border-box",
                }}
              />
            </div>
          )}

          {/* List Title */}
          <div>
            <label style={{ display: "block", fontSize: "13px", fontWeight: 700, color: "#334155", marginBottom: "6px" }}>
              Applicant List Title *
            </label>
            <input
              type="text"
              placeholder="e.g. List of Eligible Candidates - Lecturer in CS"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              required
              style={{
                width: "100%",
                padding: "9px 12px",
                borderRadius: "6px",
                border: "1px solid #cbd5e1",
                fontSize: "13.5px",
                boxSizing: "border-box",
              }}
            />
          </div>

          {/* Category */}
          <div>
            <label style={{ display: "block", fontSize: "13px", fontWeight: 700, color: "#334155", marginBottom: "6px" }}>
              Category / List Type
            </label>
            <select
              value={formData.category}
              onChange={(e) => setFormData({ ...formData, category: e.target.value })}
              style={{
                width: "100%",
                padding: "9px 12px",
                borderRadius: "6px",
                border: "1px solid #cbd5e1",
                fontSize: "13.5px",
                boxSizing: "border-box",
                backgroundColor: "#fff",
              }}
            >
              {CATEGORY_OPTIONS.map((cat) => (
                <option key={cat} value={cat}>
                  {cat}
                </option>
              ))}
            </select>
          </div>

          {/* PDF File Upload */}
          <div>
            <label style={{ display: "block", fontSize: "13px", fontWeight: 700, color: "#334155", marginBottom: "6px" }}>
              PDF Document {isEditing ? "(Leave blank to keep existing)" : "*"}
            </label>
            <input
              type="file"
              accept="application/pdf,.pdf"
              onChange={handleFileChange}
              style={{
                width: "100%",
                padding: "8px",
                borderRadius: "6px",
                border: "1px dashed #cbd5e1",
                backgroundColor: "#f8fafc",
                fontSize: "13px",
                boxSizing: "border-box",
              }}
            />
            {selectedFile && (
              <div style={{ marginTop: "6px", fontSize: "12px", color: "#16a34a", fontWeight: 600 }}>
                Selected: {selectedFile.name} ({(selectedFile.size / (1024 * 1024)).toFixed(2)} MB)
              </div>
            )}
            {isEditing && !selectedFile && existingPdfUrl && (
              <div style={{ marginTop: "6px", fontSize: "12px", color: "#64748b" }}>
                Current PDF:{" "}
                <a href={existingPdfUrl} target="_blank" rel="noopener noreferrer" style={{ color: "#2563eb" }}>
                  View current file
                </a>
              </div>
            )}
          </div>

          {/* Sort Order & Active */}
          <div style={{ display: "flex", gap: "16px", alignItems: "center" }}>
            <div style={{ flex: 1 }}>
              <label style={{ display: "block", fontSize: "13px", fontWeight: 700, color: "#334155", marginBottom: "6px" }}>
                Display Order
              </label>
              <input
                type="number"
                value={formData.sortOrder}
                onChange={(e) => setFormData({ ...formData, sortOrder: parseInt(e.target.value) || 0 })}
                style={{
                  width: "100%",
                  padding: "8px 12px",
                  borderRadius: "6px",
                  border: "1px solid #cbd5e1",
                  fontSize: "13.5px",
                  boxSizing: "border-box",
                }}
              />
            </div>

            <div style={{ flex: 1, display: "flex", alignItems: "center", gap: "8px", marginTop: "22px" }}>
              <input
                type="checkbox"
                id="isActiveCheckbox"
                checked={formData.isActive}
                onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                style={{ width: "18px", height: "18px", cursor: "pointer" }}
              />
              <label htmlFor="isActiveCheckbox" style={{ fontSize: "13.5px", fontWeight: 600, color: "#334155", cursor: "pointer" }}>
                Visible on Website
              </label>
            </div>
          </div>

          {/* Downloadable Checkbox */}
          <div
            style={{
              display: "flex",
              alignItems: "flex-start",
              gap: "10px",
              padding: "12px 14px",
              backgroundColor: formData.isDownloadable ? "#f0fdf4" : "#fffbeb",
              borderRadius: "8px",
              border: `1px solid ${formData.isDownloadable ? "#bbf7d0" : "#fef3c7"}`,
              transition: "all 0.2s ease",
            }}
          >
            <input
              type="checkbox"
              id="isDownloadableCheckbox"
              checked={formData.isDownloadable}
              onChange={(e) => setFormData({ ...formData, isDownloadable: e.target.checked })}
              style={{ width: "18px", height: "18px", cursor: "pointer", marginTop: "2px" }}
            />
            <div>
              <label
                htmlFor="isDownloadableCheckbox"
                style={{ fontSize: "13.5px", fontWeight: 700, color: "#1e293b", cursor: "pointer", display: "block" }}
              >
                Downloadable (Allow PDF Download)
              </label>
              <p style={{ margin: "2px 0 0 0", fontSize: "12px", color: "#64748b" }}>
                Selected by default. If unchecked, the PDF will be restricted to view-only on the website (downloading and copying will be disabled).
              </p>
            </div>
          </div>

          {/* Buttons */}
          <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px", marginTop: "12px" }}>
            <button
              type="button"
              onClick={() => setShowModal(false)}
              disabled={buttonLoading}
              style={{
                padding: "9px 18px",
                backgroundColor: "#f1f5f9",
                border: "1px solid #cbd5e1",
                borderRadius: "6px",
                color: "#475569",
                fontWeight: 600,
                fontSize: "13.5px",
                cursor: "pointer",
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={buttonLoading}
              style={{
                padding: "9px 22px",
                backgroundColor: "#2563eb",
                border: "none",
                borderRadius: "6px",
                color: "#fff",
                fontWeight: 600,
                fontSize: "13.5px",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              {buttonLoading && <CircularProgress size={16} color="inherit" />}
              <span>{isEditing ? "Update List" : "Upload List"}</span>
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default CareerApplicants;
